/**
 * @fileoverview Active Organisation Context
 *
 * Purpose: Manages the currently selected organisation for the authenticated user.
 * Persists the selection in localStorage so it survives page refreshes.
 * Auto-selects the first org if the user only has one.
 *
 * The provider component (`ActiveOrgProvider`) lives in `./ActiveOrgProvider`
 * — react-refresh/only-export-components wants a file to export either
 * components or values, never both.
 *
 * Dependencies:
 * - Convex: organizations.queries.getUserOrganizations
 * - TanStack Query for data fetching
 *
 * Used by:
 * - App sidebar (org switcher dropdown)
 * - Job queries (pass orgId to filter jobs)
 * - Document queries (scoped to org)
 */

import { createContext, useContext } from "react";
import type { Id } from "@cvx/_generated/dataModel";
import type { OrgRole } from "@cvx/organizations/orgMemberships/schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OrgWithRole {
  _id: Id<"organizations">;
  _creationTime: number;
  workOsOrgId: string;
  name: string;
  slug?: string;
  role: OrgRole;
}

export interface ActiveOrgContextValue {
  /** The currently selected organisation ID, or null if none selected */
  orgId: Id<"organizations"> | null;
  /** The currently selected organisation, or null */
  activeOrg: OrgWithRole | null;
  /** All organisations the user belongs to */
  userOrgs: OrgWithRole[];
  /** Whether the orgs are still loading */
  isLoading: boolean;
  /** Switch to a different organisation */
  setOrgId: (orgId: Id<"organizations">) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export const ActiveOrgContext = createContext<ActiveOrgContextValue | null>(
  null,
);

export const LOCAL_STORAGE_KEY = "morgan_active_org_id";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Returns the active organisation context.
 *
 * @returns Active org ID, org details, all user orgs, and setter
 * @throws Error if used outside ActiveOrgProvider
 *
 * @example
 * const { orgId, activeOrg, userOrgs, setOrgId } = useActiveOrg();
 */
export function useActiveOrg() {
  const ctx = useContext(ActiveOrgContext);
  if (!ctx) {
    throw new Error("useActiveOrg must be used within an ActiveOrgProvider");
  }
  return ctx;
}
